import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../api/api";
import { account_form, account_invoice, account_wf, FeatureType } from "../../types";
import { first } from "../../core/utils/array";
import { useConditional } from "../../core/hooks/useConditional";
import { EditWorkflowModal } from "./EditWorkflowModal";
import { Icon } from "../../core/ui/icons";
import { sortAlphaDesc } from "../../core/utils/sort";
import { Tab, TabContainer } from "../../core/ui/Tab";
import { hasRole, isImpersonating } from "../../core/hooks/useSession";
import { Modal } from "../../core/ui/Modal";
import { expectArray, formatBytes, formatHistoricalDate } from "rant-utils";
import { Spinner } from "../../core/ui/Spinner";
import { useGlobalHandler } from "../../core/hooks/useGlobalHandler";
import { broadcast, useChannel } from "../../core/hooks/useChannel";
import { closest, closestKey } from "../../core/utils/dom";
import { newID } from "../../core/utils/unique";
import { InlinePresenter } from "../../data-entry/InlinePresenter";
import { DataContext, useDataContext, useDataEvents } from "../../core/hooks/useData";
import { EditPaymentMethod, GatewayForm } from "../../core/form/GatewayForm";
import { InlineReport } from "../../data-entry/InlineReport";
import { useQueryState } from "../../core/hooks/useQueryState";
import { go } from "../../core/route/Route";
import { useLocalStorage } from "../../core/hooks/useLocalStorage";
import { Radio } from "../../core/form/Radio";
import { useRefresh } from "../../core/hooks/useRefresh";
import { formatCurrency } from "../../formatters/format";
import { newNotification } from "../../core/ui/Notification";

interface Phase {
	id: string,
	title?: string | any,
}

const phases = [
	{ id: "In Progress" },
	{ id: "attention", title: <span className="flex horiz gap-3"><Icon type="warning" color="error-secondary" /> Requires Attention</span>, className: "attention", onlyShowWithItems: true },
	{ id: "Saved" },
	{ id: "Complete", title: "Completed" },
];

const channelName = "current-forms";

const CurrentFormsMessages = {
	SHOW_FEATURE: "feature/show",
};

export function CurrentForms(props: {

}) {

	const [data, setData] = React.useState<{
		awfs: account_wf[],
		counts: { [k: string]: number },
		lastRefresh: string,
		phase: Phase,
		token?: string,
	}>();

	//const [phase, setPhase] = React.useState<Phase>(phases[0]);
	const [phaseID, setPhaseID] = useQueryState("phase", { persist: true });
	let phase: Phase | undefined;
	for(let p of phases) {
		if (p.id === phaseID) {
			phase = p;
			break;
		}
	}
	if (!phase) phase = phases[0];

	const [token, setToken] = React.useState("");

	const payModal = useConditional((awf: account_wf) => {
		return (
			<PayModal
				awf={awf}
				onClose={payModal.close}
				onPaid={() => {
					// reload dashboard
				}}
			/>
		)
	});

	React.useEffect(() => {
		const didPhaseChange = (data && (data.phase !== phase));
		const didTokenChange = (data && (data.token !== token));

		function getLatest() {

			// don't refresh if pay modal is open
			if (payModal.isShowing()) return;

			const lastRefresh = (didPhaseChange ? "" : data?.lastRefresh);

			api.account_wf_active({ phase: (phase ? phase.id : undefined), lastRefresh, }).then((result) => {
				// sort by most recent at the top
				if (!Array.isArray(result.awfs)) result.awfs = [];
				
				let newData = {
					...data,
					phase,
					token,

					// TODO: for now just getting full refresh each time
					// lastRefresh: result.lastRefresh,
				} as any;
				if (result.counts) newData.counts = result.counts;
				if (result.awfs) newData.awfs = result.awfs;

				// any updated or new items this refresh?
				// if (result.awfs) {
				// 	if (!data || !data.awfs || didPhaseChange || !lastRefresh) {
				// 		// have nothing locally so use from server
				// 		newData.awfs = result.awfs;

				// 	} else {
				// 		for(let a of result.awfs) {
				// 			let wasFound = false;
				// 			for(let i = 0;i<data.awfs.length;i++) {
				// 				const existing = data.awfs[i];
								
				// 				for(let a of result.awfs) {
				// 					if (existing.id === a.id) {
				// 						// just update this one inline
				// 						wasFound = true;
				// 						data.awfs[i] = a;
				// 						break;
				// 					}

				// 				}
				// 			}
				// 			if (!wasFound) {
				// 				// must be new
				// 				data.awfs.push(a);
				// 			}
				// 		}
				// 	}

				// }

				newData.awfs.sort(sortAlphaDesc("updated"));

				setData(newData);

			}).catch(() => {

			});
		}

		// if we don't have any data yet then get latest immediately
		if (!data || didPhaseChange || didTokenChange) {
			getLatest();
		} else {
			const h = setInterval(() => {
				getLatest();
			}, 20000);	
			return () => {
				clearTimeout(h);
			}
		}

	}, [phaseID, data, token, payModal]);

	const editWorkflowModal = useConditional((awf: account_wf) => {
		return (
			<EditWorkflowModal
				awf={awf}
				onCancel={editWorkflowModal.close}
				onSave={(obj) => {
					api.account_wf_update({
						id: obj.id, 
						title: obj.title,
					}).then((result: account_wf) => {
						// transfer across updated props
						awf.title = result.title;
						editWorkflowModal.close();
					}).catch((e) => {

					});
				}}
			/>
		)
	});

	const confirmDeleteModal = useConditional((awf: account_wf) => {
		return (
			<Modal
				title="Confirm Delete"
				showDelete={true}
				showSave={false}
				size="width-25vw"

				onCancel={() => {
					confirmDeleteModal.close();
				}}

				confirmOnDelete={false}
				onDelete={() => {
					api.account_wf_delete({ id: awf.id, }).then(() => {
						setToken(newID());
						confirmDeleteModal.close();
					}).catch((e) => {
						debugger;
					});
				}}
			>
				Are you sure you want to delete this order?
			</Modal>
		)
	});

	const featureModal = useConditional((msg: any) => {
		const feature = msg.feature as FeatureType;

		if (feature === "asic-data") {
			return <ASICDataModal awf={msg.awf} onClose={featureModal.close} />;
		}
	});

	useChannel(channelName, useCallback((msg) => {
		if (msg.type === CurrentFormsMessages.SHOW_FEATURE) {
			featureModal.show(msg);
		}
	}, []));


	const byPhase: { [k: string]: account_wf[] } = {};
	const items = [];

	function handleChange() {
		setToken(newID());
	}

	function handleDelete(awf: account_wf) {
		confirmDeleteModal.show(awf);
	}

	function handleEdit(awf: account_wf) {
		editWorkflowModal.show(awf);
	}

	function handlePay(awf: account_wf) {
		payModal.show(awf);
	}

	if (data && data.awfs && data.awfs.length > 0) {
		// autoRefreshIDs.current = [];

		for(let awf of data.awfs) {
			const phase = awf.state.phase;
			if (!byPhase[phase]) byPhase[phase] = [];
			byPhase[phase].push(awf);
		}
	
		for(let p of phases) {
			const toDisplay = byPhase[p.id];
			if (!toDisplay) continue;

			let i = 0;
			for(let awf of toDisplay) {
				
				items.push(
					<WFItem
						defaultExpanded={(i === 0)}

						key={awf.id + awf.updated} 
						awf={awf}
						onChange={handleChange}
						onDelete={handleDelete}
						onEdit={handleEdit}
						onPay={handlePay}
					/>
				);
				i++;

			}
		}

	}

	// console.log("autoRefreshIDs: " + autoRefreshIDs.current.join(", "));

	return (
		<div>
			<PhaseList
				phaseID={phaseID}
				counts={data ? data.counts : {}}
				onChange={(phase) => {
					setPhaseID(phase.id);
				}}
			/>

			<div className="flex vert align-start padding-10" style={{borderTop: "solid #eee 1px"}}>
				{items}
			</div>

			{editWorkflowModal.render()}
			{confirmDeleteModal.render()}
			{payModal.render()}
			{featureModal.render()}
		</div>
	);
}

const ASICSegmentNames = {
	"ZHD": "Header",
	"ZCO": "Company details",
	"ZNR": "Name reservation",
	"ZPR": "Business names owned",
	"ZRG": "Registered office",
	"ZFY": "Standard hours",
	"ZOH": "Office hours",
	"ZRP": "PPOB",
	"ZUH": "Ultimate holding company",

	"ZSD": "Personal details",
	"ZFN": "Former names",
	"ZOF": "Offices held",

	//"ZSC": "Member guarantee",
	"ZSC": "Share classes",

	"ZHH": "Holding",
	"ZSH": "Member",
	"ZNS": "Non share members",
	
	"ZCG": "Applicant",
	"ZDC": "Applicant signature",
	"ZAM": "Administrative details",
	
	"ZTE": "Manual review text",
	
	"ZTR": "Trailer",
} as any;

export function ASICDataModal(props: {
	awf: account_wf,
	onClose: () => void,
}) {

	const [data, setData] = useState<any>();

	useEffect(() => {
		api.account_wf_asic_data({ awf: props.awf.id }).then((result) => {
			setData(result);

		}).catch(() => {

		});

	}, []);

	const formatted = [];
	const lines = (data && data.asData) ? data.asData.split("\n") : [];
	for(let l of lines) {
		const seg = l.substring(0,3);

		formatted.push(
			<tr>
				<td><div className="tag">{ASICSegmentNames[seg]}</div></td>
				<td>{l.substring(3).replace(/\t/g, " ")}</td>
			</tr>
		);
	}

	return (
		<Modal
			title="ASIC Data"
			size="width-100pc"
			onCancel={props.onClose}
			showSave={false}
			cancelOnEscapeKey={true}
		>
			<TabContainer
				displayStyle="border-indent"
			>
				<Tab title="Data">
					<div className="pre">
						<table>
							<thead>
								<th></th>
							</thead>

							<tbody>
								{formatted}
							</tbody>
						</table>
					</div>
				</Tab>
				<Tab title="JSON">
					<div className="pre">
						{data && JSON.stringify(data.asJSON, null, 4)}
					</div>
				</Tab>
			</TabContainer>
		</Modal>
	);
}

export function UpdateEntityFeature(props: {
	awf: account_wf,
}) {

	return (
		<>
			<li
				onClick={() => {
					const form = first(props.awf.resources.forms);

					api.entity_update_from_form({
						type: "company",
						form,
					})
				}}
			>
				<Icon type="next" size="small" />Update Entity
				<Icon type="lock" size="small" />
			</li>
		</>
	);
}

export function ASICDataFeature(props: {
	awf: account_wf,
}) {
	return (
		<>
			<li
				onClick={(e) => {
					broadcast(channelName, {
						type: CurrentFormsMessages.SHOW_FEATURE,
						feature: "asic-data",
						awf: props.awf,
					});

				}}
			>
				<Icon type="next" size="small" />View ASIC Data
				<Icon type="lock" size="small" />
			</li>
		</>
	)
}

export function WFItem(props: {
	awf: account_wf, 

	defaultExpanded: boolean,

	onChange?: () => void,
	onDelete?: (awf: account_wf) => void,
	onPay?: (awf: account_wf) => void,
	onEdit?: (awf: account_wf) => void,
}) {

	const { awf } = props;
	const buttons = [];

	const [isExpanded, setIsExpanded] = useState(props.defaultExpanded);

	// state transition buttons
	if (awf.state && awf.state.def) {
		let defaultTransitionButton;

		for(let t of awf.state.def.transitions) {
			// is a role required?
			if (t.role && !hasRole(t.role)) continue;

			if (!t.availableWithFormErrors && (awf.incompleteForm !== false)) continue;

			if (t.hidden && (!hasRole("admin") && !isImpersonating())) continue;

			let title: any = t.actionTitle;
			if (t.hidden) {
				title = <i>{title}<Icon type="lock" size="small" /></i>;
			}

			const btn = 
				<li
					key={t.actionTitle}
					onClick={() => {
						if (t.requestPayment) {
							if (props.onPay) props.onPay(awf);

						} else {
							api.account_wf_transition({ id: awf.id, toState: t.toState }).then(() => {
								if (props.onChange) props.onChange();
					
							}).catch(() => {
					
							});	
						}
					}}
				>
					<Icon type="next" size="small" />{title}
				</li>
			;

			if (t.isDefault) {
				defaultTransitionButton = btn;
			} else {
				buttons.push(btn);			
			}

		}

		if (defaultTransitionButton) {
			buttons.splice(0, 0, defaultTransitionButton);
		}


		// features
		if (awf.state.def.features) {
			for(let f of awf.state.def.features) {
				if (f.adminOnly && (!hasRole("admin") && !isImpersonating())) continue;

				if (f.feature === "asic-data") {
					buttons.push(
						<ASICDataFeature awf={awf} key={"feature-" + f.feature} />
					);

				} else if (f.feature === "update-entity") {
					buttons.push(
						<UpdateEntityFeature awf={awf} key={"feature-" + f.feature} />
					);
				}

			}
		}

	}

	let formStatus;

	// edit button
	const f = first(awf.resources.forms);
	if (f && awf.state && awf.state.def) {
		if (awf.state.def!.canEditForms) {
			// workflow with a form

			if (awf.state.def!.canDeleteForms) {
				buttons.push(
					<div className="sep" key='sep-delete' />,
					<li
						key={"delete"}
						onClick={() => {
							if (props.onDelete) props.onDelete(awf);
						}}
					><Icon size="small" type="delete" />Delete</li>
				);
			}

			buttons.splice(
				0, 0,
				<li
					key={"edit"}
					className="primary" //primary
					onClick={() => {
						go(`/form/${f}`);
						//document.location.href = `/form/${f}`;
					}}
				><Icon size="small" type="edit" />Edit</li>,
				<div className="sep" key="sep-edit" />
			);

			// is it incomplete?
			if (awf.incompleteForm === false) {
				formStatus = <div className="tag success" key="complete"><Icon type="tick" size="tiny" />&nbsp;Ready</div>;
			} else {
				formStatus = <div className="tag incomplete" key="incomplete">Incomplete</div>;
			}
		}

	} else {
		const editWorkflow = (awf.state.def && awf.state.def!.editWorkflow);
		if (editWorkflow && editWorkflow.allow) {
			if (!editWorkflow.role || (editWorkflow.role && hasRole(editWorkflow.role))) {
				// workflow with no forms
				buttons.splice(
					0, 0,
					<li
						key={"edit"}
						className="primary" //primary
						onClick={() => {
							if (props.onEdit) props.onEdit(awf);
						}}
					><Icon size="small" type="edit" />Edit</li>,
					<div className="sep" key="sep-edit" />
				);
			}
		}

	}

	const docs = [];
	if (awf.resources.docs && awf.resources.docs.length > 0) {
		for(let d of awf.resources.docs) {
			if (d.event) {
				docs.push(
					<div key={d.event} className="flex horiz align-center gap-2 margin-right-5">
						{/* <Icon type="document" /> */}
						<img src="/img/logo-asic.png" width="20" height="20" />
						<a target="_blank" href={`/event/${d.event}`}>{d.title}</a>
						{/* <div className="tag subtle">{formatHistoricalDate(d.created)}</div> */}


						{
							hasRole("admin") &&
							<a target="_blank" href={`/event/${d.event}/?showFields=1`}><Icon type="field" /></a>
						}

					</div>
				);

			} else {
				docs.push(
					<div key={d.blob} className="flex horiz align-center gap-2 margin-right-5">
						<Icon type="document" />
						<a target="_blank" href={`/api/account_resource/${d.blob}`}>{d.title}</a>
						{/* <div className="tag subtle">{formatHistoricalDate(d.created)}</div>
						<div className="tag subtle">{formatBytes(d.sizeBytes)}</div> */}

					</div>
				);

			}
		}
	}
	if (awf.resources.urls && awf.resources.urls.length > 0) {
		for(let u of awf.resources.urls) {
			let url = u.url;
			if (f) url = url.replace("{form}", f);

			let title = u.title;

			// want to embed the title in the pdf generation url
			// this allows the browser to choose a more sensible default filename
			const fn = title.replace("{title}", awf.title);
			url += "?fn=" + fn;


			// cut out the "{title} - " .. wait to compact it a bit
			title = u.title.replace("{title} - ", "");
			//let title = u.title.replace("{title}", awf.title);

			docs.push(
				<div key={u.url} className="flex gap-2 margin-right-5">
					<Icon type="document" />
					<a target={u.title} href={url}>{title}</a>

					{
						hasRole("admin") &&
						<a target={u.title} href={url + "&showFields=1"}><Icon type="field" /></a>
					}

				</div>
			);
		}
	}

	const stateName = (awf.state.def ? awf.state.def!.title : awf.state.current);
	const isError = (stateName.indexOf("Failed") >= 0);

	let options;
	if (isExpanded) {
		options = (
			<div className="details flex horiz gap-2 flow-row-wrap">
				{/* {
					awf.state.def?.waitingForCompletion &&
					<div className="flex align-center gap-5">
						<span className="tag success">Processing</span>
						<Spinner size="small" />
					</div>
				} */}
						{
							awf.state.def?.waitingForCompletion &&
							<Spinner size="small" />
						}

				<div className={"tag has-title " + (isError ? "error " : "") + (awf.state.def ? awf.state.def!.id : "")}>
					<span className="title">
						{stateName}
					</span>
					<span className="text">{formatHistoricalDate(awf.updated)}</span>
				</div>
				{formStatus}

				{
					((!awf.state.def?.waitingForCompletion || hasRole("admin")) && buttons.length > 0) &&
					<div className="buttons flex horiz gap-5 relative awf" data-key={awf.id}>
						<OptionsButton>
							{buttons}
						</OptionsButton>
					</div>
				}

			</div>
		);
	}

	let documents;
	if (isExpanded && (docs && docs.length > 0)) {
		documents = (
			<div
				className="order-collection"
			>
				{docs}
				{
					docs && docs.length > 0 &&
					<a className="cancel flex horiz gap-2" href="#" target="_blank" onClick={(e) => {
						e.preventDefault();
						const w = window.open("/pdf-download/" + awf.id + "/", "_blank");
						//document.location.href = "/pdf-download/" + awf.id + "/";
					}}><Icon type="download" />Download <div className="count primary">{docs.length}</div></a>
				}
				{/* <AccountWorkflowHistory
					account_wf={awf.id}
				/> */}
			</div>
		);
	}

	return (
		<div
			key={awf.id + awf.updated}
			className={"wf-item flex vert align-start gap-2 " + (isExpanded ? "" : "collapsed")}
			onClick={() => {
				setIsExpanded(!isExpanded);
			}}
		>
			
			<div className="flex horiz align-start gap-5">
				<WorkflowIcon icon={awf.state.icon} />

				<div className="flex vert align-start gap-2" style={{paddingTop:"3px"}}>
					<b
						className="wf-title"
						onClick={async () => {
							await navigator.clipboard.writeText(awf.id);
						}}
						//style={{fontSize:"16px"}}
					>
						{awf.title ? awf.title : "Unnamed ?"}
						{(!isExpanded && awf.incompleteForm) && <Icon type="edit" color="warning" size="small" />}
						{(!isExpanded && !awf.incompleteForm) && <Icon type="tick" color="success" size="small" />}
					</b>

					{options}
					{documents}
				</div>
			</div>


			

		</div>
	);

}

export function WorkflowIcon(props: {
	icon?: string,
}) {

	let actualIcon;
	if (props.icon) {
		// is this a name of an actual icon, or upper case char(s)?
		const firstChar = props.icon.substring(0,1);
		if (
			firstChar === firstChar.toLowerCase()
			&& (
				firstChar < "0" || firstChar > "9"
			)
		) {
			// icon
			actualIcon = <Icon type={props.icon as any} />
		} else {
			actualIcon = <div>{props.icon}</div>;
		}
	} else {
		actualIcon = <div>?</div>;
						
	}
	
	return (
		<div className="product-icon text-inverse">
			{actualIcon}
		</div>
	);

}

export function OptionsButton(props: {
	children?: any,
}) {

	const [showingOptions, setShowingOptions] = useState(false);
	// const ref = useRef<HTMLDivElement>(null);

	// useEffect(() => {
	// 	if (!showingOptions) return;

	// 	function handler(e: any) {
	// 		const el = closest(e.target, ".wf-options");
	// 		if (!el || el !== ref.current) {
	// 			setShowingOptions(false);
	// 		}
	// 	}

	// 	window.addEventListener("mousedown", handler);
	// 	return () => {
	// 		window.removeEventListener("mousedown", handler);
	// 	}
	// }, [showingOptions]);

	useEffect(() => {
		if (!showingOptions) return;

		function handler(e: any) {
			setTimeout(() => {
				setShowingOptions(false);
			}, 200);
		}

		window.addEventListener("mousedown", handler);
		return () => {
			window.removeEventListener("mousedown", handler);
		}
	}, [showingOptions]);

	return (
		<>
			<button className="small flex gap-2 options-button"
				onClick={(e) => {
					setShowingOptions(!showingOptions);
					e.stopPropagation();
				}}
			><Icon type="arrowDown" size="small" />Options</button>

			{
				showingOptions &&
				<div
					className={"wf-options"}
					//ref={ref}
				>
					{props.children}
				</div>
			}
		</>
	);
}


export function PhaseList(props: {
	counts: { [k: string]: number },
	phaseID: string,
	onChange: (phase: Phase) => void,
}) {

	let initialIndex = 0;

	const tabs = [];

	for(let i=0;i<phases.length;i++) {
		const p = phases[i];

		if (p.id === props.phaseID) initialIndex = i;

		const count = <span className="count font-small">{props.counts[p.id]}</span>;
		
		tabs.push(
			<Tab 
				className={p.className}
				hidden={(p.onlyShowWithItems && props.counts[p.id] <= 0)}
				title={<div className={"flex horiz gap-3 " + p.className}><span>{p.title ? p.title : p.id}</span>{count}</div>} key={p.id} 
			/>
		);
	}


	return (
		<TabContainer
			displayStyle="border"
			initialIndex={initialIndex}
			onChange={(index) => {
				props.onChange(phases[index]);
			}}
		>
			{tabs}
		</TabContainer>
	)
}

export function PayModal(props: {
	awf: account_wf,

	onClose: () => void,
	onPaid: () => void,
}) {
	const data = useDataContext({
		initialValue: {
			paymentType: "card",
			paymentTime: "0"
		},
	});

	const refresh = useRefresh();

	useDataEvents(data, useCallback(() => {
		refresh.refresh();
	}, []));

	// const [af, setAF] = useState<account_form>();
	const [invoices, setInvoices] = useState<{
		amountPaymentRequired: number,
		invoices: account_invoice[]
	}>();

	const [account] = useLocalStorage<any>("account");

	// load the form because need invoice info from it
	useEffect(() => {
		const f = first(props.awf.resources.forms);
		if (!f) return;

		// api.account_form_get({
		// 	id: f,
		// }).then((form) => {
		// 	setAF(first(form));
		// 	data.validate();
		// }).catch((e) => {

		// });

		// always call reconcile
		// it will get any previously paid and draft invoices
		api.account_invoice_reconcile({
			awf: props.awf.id,
		}).then((res) => {
			const invoices = expectArray(res);
			let amountPaymentRequired = 0;

			for(let i of invoices) {
				if (!i.paid) amountPaymentRequired += i.total;
			}
	
			setInvoices({
				invoices,
				amountPaymentRequired,
			});

		}).catch((e) => {

		});	


	}, []);


	const paymentOptions = [
		{ id: "card", name: "Card" },
		{ id: "bank-deposit", name: "Bank Deposit" },
	];

	const canUseECC = account && account.options && account.options.enduringCard;
	const hasExtendedCredit = account && account.options && account.options.extendedCredit;
	if (canUseECC) {
		paymentOptions.push({ id: "enduring-card", name: "Enduring Credit Card" });
	}
	if (hasExtendedCredit) {
		paymentOptions.push({ id: "extended-credit", name: "Extended Credit" });
	}

	let saveTitle = "Continue";
	let saveEnabled = true;
	const paymentType = data.getValue("paymentType");
	if (paymentType == "card" && invoices && invoices.amountPaymentRequired > 0) saveTitle = `Pay ${formatCurrency(invoices.amountPaymentRequired)} Now`;
	if (!paymentType || paymentType === "bank-deposit") {
		saveEnabled = false;
	}

	return (
		<DataContext.Provider value={data}>
			<Modal
				title={"Pay"}
				saveTitle={saveTitle}
				size="width-50vw" //"width-75vw"
				onCancel={props.onClose}
				showSave={(saveEnabled)}
				onSave={() => {

					let paymentType = data.getValue("paymentType");
					if (paymentType === "enduring-card") {
						paymentType += "-" + data.getValue("paymentTime");
					}

					// depending on payment type either we pay immediately or a deferred option
					const payAPI = 
						(data.getValue("paymentType") === "card") ? 
						api.payment_awf({ awf: props.awf.id })
						: api.payment_defer_awf({ awf: props.awf.id, paymentType, });

					payAPI.then(() => {
						data.setApiStatus({ communicating: false });

						// reload the page
						document.location.reload();
						//props.onClose();

					}).catch(() => {
						data.setApiStatus({ communicating: false });						
					});

					data.setApiStatus({ communicating: true });

					//props.onClose();
				}}
			>

				{
					(paymentOptions.length > 1) &&
					<p className="margin-bottom-5">Please choose from the following payment options:</p>
				}

				{
					(paymentOptions.length > 1) &&
					<div className="bg-subtle padding-5">
						<Radio 
							options={paymentOptions}
							prop="paymentType"
							getDisplay={(item, selected) => {
								return item ? item.name : "";
							}}	
							getValue={(item: any) => {
								return item ? item.id : "";
							}}
						/>

						{
							(data.getValue("paymentType") == "card") &&
							<div className="padding-5">
								<EditPaymentMethod onClose={() => {}} />
							</div>
						}

						{
							(data.getValue("paymentType") == "bank-deposit") &&
							<div className="padding-5">
								***Bank account details to be provided here...<br/>
								And some info about once payment received your order will be progressed?
								Disable Continue button? Don't want to allow user to proceed - needs admin to do it?
								What reference id etc is to be used on the transfer?
							</div>
						}

						{
							(data.getValue("paymentType") == "enduring-card") &&
							<div className="padding-5">
								<p>Please choose when you would like the payment to be deducted:</p>
								<Radio 
									verticalLayout={true}
									options={[
										{ id: "0", name: "Pay immediately" },
										{ id: "14", name: "Pay in 14 days" },
									]}
									prop="paymentTime"
									getDisplay={(item, selected) => {
										return item ? item.name : "";
									}}	
									getValue={(item: any) => {
										return item ? item.id : "";
									}}
								/>								
								<p>Any other info to show here?</p>
							</div>
						}

						{
							(data.getValue("paymentType") == "extended-credit") &&
							<div className="padding-5">
								Info about paying in 30 days
							</div>
						}


					</div>
				}


				{
					invoices &&
					<InlineReport
						reportID="invoice-summary"
						data={invoices}
					/>
				}

			</Modal>
		</DataContext.Provider>
	)
}